// API client helper
const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

// Helper function to make API requests
const apiRequest = async (endpoint, options = {}) => {
  const url = `${API_BASE_URL}${endpoint}`;
  const isFormData = options.body instanceof FormData;
  
  const config = {
    headers: {
      ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
      ...options.headers,
    },
    ...options,
  };
  
  // Add authorization header if token exists
  if (typeof window !== 'undefined') {
    const token = localStorage.getItem('admin_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
  }
  
  try {
    const response = await fetch(url, config);
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || 'Something went wrong');
    }
    
    return await response.json();
  } catch (error) {
    console.error(`API Error (${endpoint}):`, error);
    throw error;
  }
};

// Auth API
export const authApi = {
  login: (credentials) => apiRequest('/auth/login', {
    method: 'POST',
    body: JSON.stringify(credentials),
  }),
  
  getCurrentUser: () => apiRequest('/auth/me'),
};

// Users Management API (Superadmin)
export const usersApi = {
  getAll: (params = {}) => {
    const queryParams = new URLSearchParams(params).toString();
    return apiRequest(`/admin/users/${queryParams ? `?${queryParams}` : ''}`);
  },

  getById: (id) => apiRequest(`/admin/users/${id}`),

  create: (data) => apiRequest('/admin/users/', {
    method: 'POST',
    body: JSON.stringify(data),
  }),

  update: (id, data) => apiRequest(`/admin/users/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),

  delete: (id) => apiRequest(`/admin/users/${id}`, {
    method: 'DELETE',
  }),

  // Profile endpoints (any authenticated user)
  getProfile: () => apiRequest('/admin/users/profile/me'),

  updateProfile: (data) => apiRequest('/admin/users/profile/me', {
    method: 'PUT',
    body: JSON.stringify(data),
  }),

  changePassword: (data) => apiRequest('/admin/users/profile/change-password', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
};

// Categories API
export const categoriesApi = {
  getAll: (params = {}) => {
    const queryParams = new URLSearchParams(params).toString();
    return apiRequest(`/categories/${queryParams ? `?${queryParams}` : ''}`);
  },
  
  getAllAdmin: (params = {}) => {
    const queryParams = new URLSearchParams(params).toString();
    return apiRequest(`/categories/admin${queryParams ? `?${queryParams}` : ''}`);
  },
  
  getById: (id) => apiRequest(`/categories/admin/${id}`),
  
  create: (data) => apiRequest('/categories/admin', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  
  update: (id, data) => apiRequest(`/categories/admin/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
  
  delete: (id) => apiRequest(`/categories/admin/${id}`, {
    method: 'DELETE',
  }),
};

// Products API
export const productsApi = {
  getAll: (params = {}) => {
    const queryParams = new URLSearchParams(params).toString();
    return apiRequest(`/products/${queryParams ? `?${queryParams}` : ''}`);
  },
  
  getBySlug: (slug) => apiRequest(`/products/slug/${slug}`),
  
  getAllAdmin: (params = {}) => {
    const queryParams = new URLSearchParams(params).toString();
    return apiRequest(`/products/admin/${queryParams ? `?${queryParams}` : ''}`);
  },
  
  getById: (id) => apiRequest(`/products/admin/${id}`),
  
  create: (data) => apiRequest('/products/admin/', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  
  update: (id, data) => apiRequest(`/products/admin/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
  
  delete: (id) => apiRequest(`/products/admin/${id}`, {
    method: 'DELETE',
  }),

  uploadImage: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return apiRequest('/products/admin/upload-image', {
      method: 'POST',
      body: formData,
    });
  },
};

// Landing Page API
export const landingPagesApi = {
  getBySlug: (slug) => apiRequest(`/landing-pages/${slug}`),

  getTemplates: () => apiRequest('/landing-pages/templates'),

  getTemplatesAdmin: (params = {}) => {
    const queryParams = new URLSearchParams(params).toString();
    return apiRequest(`/landing-pages/admin/templates/${queryParams ? `?${queryParams}` : ''}`);
  },

  createTemplate: (data) => apiRequest('/landing-pages/admin/templates/', {
    method: 'POST',
    body: JSON.stringify(data),
  }),

  updateTemplate: (id, data) => apiRequest(`/landing-pages/admin/templates/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),

  getAllAdmin: (params = {}) => {
    const queryParams = new URLSearchParams(params).toString();
    return apiRequest(`/landing-pages/admin/pages/${queryParams ? `?${queryParams}` : ''}`);
  },

  create: (data) => apiRequest('/landing-pages/admin/pages/', {
    method: 'POST',
    body: JSON.stringify(data),
  }),

  update: (id, data) => apiRequest(`/landing-pages/admin/pages/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),

  updateStatus: (id, isActive, pageStatus) => apiRequest(`/landing-pages/admin/pages/${id}/status?is_active=${isActive}${pageStatus ? `&status_page=${pageStatus}` : ''}`, {
    method: 'PATCH',
  }),

  delete: (id) => apiRequest(`/landing-pages/admin/pages/${id}`, {
    method: 'DELETE',
  }),

  regenerateStatic: (id) => apiRequest(`/landing-pages/admin/pages/${id}/regenerate-static`, {
    method: 'POST',
  }),

  uploadImage: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return apiRequest('/landing-pages/admin/upload-image', {
      method: 'POST',
      body: formData,
    });
  },
};

// Dashboard API
export const dashboardApi = {
  getSummary: (params = {}) => {
    const queryParams = new URLSearchParams(params).toString();
    return apiRequest(`/admin/dashboard/summary${queryParams ? `?${queryParams}` : ""}`);
  },
};

// Admin Sales API
export const salesApi = {
  getAll: (params = {}) => {
    const queryParams = new URLSearchParams(params).toString();
    return apiRequest(`/admin/sales/${queryParams ? `?${queryParams}` : ''}`);
  },

  getSummary: () => apiRequest('/admin/sales/summary'),

  create: (data) => apiRequest('/admin/sales/', {
    method: 'POST',
    body: JSON.stringify(data),
  }),

  updateStatus: (id, data) => apiRequest(`/admin/sales/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  }),

  getDiscounts: () => apiRequest('/admin/sales/discounts/'),

  createDiscount: (data) => apiRequest('/admin/sales/discounts/', {
    method: 'POST',
    body: JSON.stringify(data),
  }),

  updateDiscount: (id, data) => apiRequest(`/admin/sales/discounts/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),

  updateDiscountStatus: (id, isActive) => apiRequest(`/admin/sales/discounts/${id}/status?is_active=${isActive}`, {
    method: 'PATCH',
  }),

  deleteDiscount: (id) => apiRequest(`/admin/sales/discounts/${id}`, {
    method: 'DELETE',
  }),
};

// Admin Shipping / RajaOngkir API
export const shippingApi = {
  getCouriers: () => apiRequest('/admin/shipping/couriers'),

  createCourier: (data) => apiRequest('/admin/shipping/couriers', {
    method: 'POST',
    body: JSON.stringify(data),
  }),

  updateCourier: (id, data) => apiRequest(`/admin/shipping/couriers/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),

  deleteCourier: (id) => apiRequest(`/admin/shipping/couriers/${id}`, {
    method: 'DELETE',
  }),

  seedDefaultCouriers: () => apiRequest('/admin/shipping/couriers/seed-default', {
    method: 'POST',
  }),

  getProvinces: (params = {}) => {
    const queryParams = new URLSearchParams(params).toString();
    return apiRequest(`/admin/shipping/locations/provinces${queryParams ? `?${queryParams}` : ''}`);
  },

  getCities: (params = {}) => {
    const queryParams = new URLSearchParams(params).toString();
    return apiRequest(`/admin/shipping/locations/cities${queryParams ? `?${queryParams}` : ''}`);
  },

  getDistricts: (params = {}) => {
    const queryParams = new URLSearchParams(params).toString();
    return apiRequest(`/admin/shipping/locations/districts${queryParams ? `?${queryParams}` : ''}`);
  },

  getSubdistricts: (params = {}) => {
    const queryParams = new URLSearchParams(params).toString();
    return apiRequest(`/admin/shipping/locations/subdistricts${queryParams ? `?${queryParams}` : ''}`);
  },

  calculateCost: (data) => apiRequest('/admin/shipping/calculate-cost', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
};

// Admin Payment Methods API
export const paymentMethodsApi = {
  getAll: () => apiRequest('/admin/payments/methods'),

  create: (data) => apiRequest('/admin/payments/methods', {
    method: 'POST',
    body: JSON.stringify(data),
  }),

  update: (id, data) => apiRequest(`/admin/payments/methods/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),

  updateStatus: (id, isActive) => apiRequest(`/admin/payments/methods/${id}/status?is_active=${isActive}`, {
    method: 'PATCH',
  }),

  delete: (id) => apiRequest(`/admin/payments/methods/${id}`, {
    method: 'DELETE',
  }),
};

// Public Checkout API
export const checkoutApi = {
  getProvinces: (params = {}) => {
    const queryParams = new URLSearchParams(params).toString();
    return apiRequest(`/checkout/locations/provinces${queryParams ? `?${queryParams}` : ''}`);
  },

  getCities: (params = {}) => {
    const queryParams = new URLSearchParams(params).toString();
    return apiRequest(`/checkout/locations/cities${queryParams ? `?${queryParams}` : ''}`);
  },

  getDistricts: (params = {}) => {
    const queryParams = new URLSearchParams(params).toString();
    return apiRequest(`/checkout/locations/districts${queryParams ? `?${queryParams}` : ''}`);
  },

  getSubdistricts: (params = {}) => {
    const queryParams = new URLSearchParams(params).toString();
    return apiRequest(`/checkout/locations/subdistricts${queryParams ? `?${queryParams}` : ''}`);
  },

  getPaymentMethods: () => apiRequest('/checkout/payment-methods'),

  getShippingRates: (data) => apiRequest('/checkout/shipping-rates', {
    method: 'POST',
    body: JSON.stringify(data),
  }),

  previewDiscount: (data) => apiRequest('/checkout/discounts/preview', {
    method: 'POST',
    body: JSON.stringify(data),
  }),

  createOrder: (data) => apiRequest('/checkout/orders', {
    method: 'POST',
    body: JSON.stringify(data),
  }),

  searchOrders: (params = {}) => {
    const queryParams = new URLSearchParams(params).toString();
    return apiRequest(`/checkout/orders${queryParams ? `?${queryParams}` : ''}`);
  },

  getOrder: (invoice) => apiRequest(`/checkout/orders/${invoice}`),
};

// Public Guest Cart API
export const cartApi = {
  get: (token) => apiRequest(`/cart/${token}`),

  upsertItem: (token, data) => apiRequest(`/cart/${token}/items`, {
    method: 'POST',
    body: JSON.stringify(data),
  }),

  removeItem: (token, productId) => apiRequest(`/cart/${token}/items/${productId}`, {
    method: 'DELETE',
  }),

  clear: (token) => apiRequest(`/cart/${token}`, {
    method: 'DELETE',
  }),
};
